/**
 * 汇率域（`rates`）契约 —— **取数与手动录入的唯一契约源**（架构 §6.1）。
 *
 * ## 口径（`汇率域口径对齐单` §2 · `DR-004`）
 * - **存量**折本币取**最新**；**流量**折本币取**交易日期** —— 同源，仅"选日期"不同；
 * - 精度 = **万分之一整数**（`RATE_SCALE = 10000`），折本币后 `round` 至**分**；
 * - 🔴 `manual` **写时覆盖**同日 `frankfurter` 行（唯一键不含 `source`，见 `enums/fx.ts`）。
 *
 * ## 拿不到的，明说
 * 缺汇率时消费方（J / K 域）产出 `fxWarnings` —— **不静默、不按 1:1 冒充**（`AC-J1.24`）。
 * 本契约**不提供"兜底汇率"字段**：没有就是没有。
 */

import { z } from 'zod';
import { RatePolicy } from '../constants/index.js';
import { RateSource } from '../enums/fx.js';
import { CurrencySchema } from './account.js';
import { DayString } from './pagination.js';

/** 单条汇率视图 —— `rate` 是**万分之一整数**，不是小数 */
export interface FxRateView {
  id: string;
  /**
   * `YYYY-MM-DD` —— 🔴 **真实报价日**。
   *
   * ⚠️ Frankfurter 对**非交易日**（周末 / 节假日）会**回退到最近交易日**并在响应
   * `date` 字段如实告知（实测：请求周日 → `date` 为周五）⇒ 库里**不会有**"周末行"。
   * 因此"按某天取汇率"须**向前回退** —— 唯一实现 = `nucleus/fx.ts` 的 `pickRateOnOrBefore()`。
   */
  date: string;
  base: string;
  quote: string;
  /** **1 单位 `base` = `rate` / 10000 单位 `quote`** */
  rate: number;
  source: RateSource;
}

/** 列表结果（🔴 **不分页** —— 汇率是有界小集合，端侧选择器要全量；与 `/api/accounts` 同例） */
export interface RateListResult {
  items: FxRateView[];
  total: number;
}

/**
 * 手动录入 / 覆盖。
 *
 * 🔴 **写时覆盖**：同 `(date, base, quote)` 的既有行（无论 `frankfurter` 还是 `manual`）
 * 被本次写入替换 —— 这是"手动优先"在 v2 唯一键下的**实现方式**。
 */
export const PutRateRequestSchema = z
  .object({
    date: DayString,
    base: CurrencySchema,
    quote: CurrencySchema,
    /** 万分之一整数：`6.7045` ⇒ `67045`（Frankfurter 恰好回 4 位小数，**天然对齐**） */
    rate: z
      .number({ message: '汇率必须是数字（万分之一整数）' })
      .int('汇率必须是整数（万分之一，如 6.7045 → 67045）')
      .positive('汇率必须大于 0')
      .max(RatePolicy.MaxRateE4, `汇率不得超过 ${RatePolicy.MaxRateE4}（万分之一）`),
  })
  .refine((d) => d.base !== d.quote, { message: '`base` 与 `quote` 不能相同（同币种汇率恒为 1）' });

export type PutRateRequest = z.infer<typeof PutRateRequestSchema>;

/** 列表查询入参（全部可选 ⇒ 查询串是字符串，用 `z.coerce` 无意义，故本 schema 只校验形态） */
export const RateQuerySchema = z.object({
  base: CurrencySchema.optional(),
  quote: CurrencySchema.optional(),
  from: DayString.optional(),
  to: DayString.optional(),
});
export type RateQuery = z.infer<typeof RateQuerySchema>;
